import React, { createContext, useContext, useMemo } from 'react'
import { FlagProvider as UnleashSdkProvider } from '@unleash/proxy-client-react'
import { AppLoader } from 'shared/AppLoader'
import { CONFIG } from 'shared/modules/featureFlagsConfig'
import { getFeatureFlagProvider } from './providersByFlag'
import {
  GrowthbookProvider,
  useGrowthbookFeatureValue,
  useGrowthbookStatus,
} from './GrowthbookProvider'
import {
  UnleashProvider,
  useUnleashFeatureValue,
  useUnleashStatus,
} from './UnleashProvider'

const FeatureFlagsRuntimeContext = createContext({
  isReady: false,
  getFlagValue: () => false,
})

const FeatureFlagsRuntimeStateProvider = ({ children }) => {
  const growthbookReady = useGrowthbookStatus()
  const unleashReady = useUnleashStatus()
  const getGrowthbookFeatureValue = useGrowthbookFeatureValue()
  const getUnleashFeatureValue = useUnleashFeatureValue()

  const value = useMemo(
    () => ({
      isReady: unleashReady && growthbookReady,
      getFlagValue: (flagName, options = {}) => {
        const provider = options.provider || getFeatureFlagProvider(flagName)

        if (provider === 'growthbook') {
          return Boolean(getGrowthbookFeatureValue(flagName, false))
        }

        return getUnleashFeatureValue(flagName)
      },
    }),
    [
      getGrowthbookFeatureValue,
      getUnleashFeatureValue,
      growthbookReady,
      unleashReady,
    ]
  )

  return (
    <FeatureFlagsRuntimeContext.Provider value={value}>
      {children}
    </FeatureFlagsRuntimeContext.Provider>
  )
}

export const useFeatureFlagsRuntime = () =>
  useContext(FeatureFlagsRuntimeContext)

const FeatureFlagsRuntimeBoundary = ({ children }) => {
  const { isReady } = useFeatureFlagsRuntime()

  return isReady ? children : <AppLoader />
}

export const FeatureFlagsRuntimeProvider = ({
  userId,
  unleashProxy,
  growthbook,
  children,
}) => (
  <UnleashSdkProvider
    config={{ ...CONFIG, ...unleashProxy, context: { userId } }}
  >
    <UnleashProvider>
      <GrowthbookProvider
        apiHost={growthbook.apiHost}
        clientKey={growthbook.clientKey}
        userId={userId}
      >
        <FeatureFlagsRuntimeStateProvider>
          <FeatureFlagsRuntimeBoundary>{children}</FeatureFlagsRuntimeBoundary>
        </FeatureFlagsRuntimeStateProvider>
      </GrowthbookProvider>
    </UnleashProvider>
  </UnleashSdkProvider>
)
